import React, {useEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {fetchShoppingCardById} from "../../../../../../reduxLib/shoppingcardLib";
import {fetchColorById} from "../../../../../../reduxLib/colorsLib";

function ViewModal({ isOpen, onClose, order }) {

    const dispatch = useDispatch();

    const [shoppingCard, setShoppingCard] = useState(null);
    const [colorsById, setColorsById] = useState({});
    const [isLoadingCard, setIsLoadingCard] = useState(false);

    useEffect(() => {
        if (!isOpen || !order?.shopingCardIdRel) return;

        const loadShoppingCard = async () => {
            try {
                setIsLoadingCard(true);

                const card = await dispatch(
                    fetchShoppingCardById(order.shopingCardIdRel)
                ).unwrap();

                setShoppingCard(card);
            } catch (error) {
                console.error("Failed to fetch shopping card", error);
                setShoppingCard(null);
            } finally {
                setIsLoadingCard(false);
            }
        };

        loadShoppingCard();
    }, [isOpen, order?.shopingCardIdRel, dispatch]);

    useEffect(() => {
        if (!shoppingCard?.productItems?.length) return;

        const loadColors = async () => {
            const colorIds = [
                ...new Set(
                    shoppingCard.productItems
                        .map((item) => item.product?.colorId)
                        .filter(Boolean)
                ),
            ];

            const colorResults = await Promise.all(
                colorIds.map(async (colorId) => {
                    try {
                        const color = await dispatch(fetchColorById(colorId)).unwrap();
                        return [colorId, color];
                    } catch (error) {
                        console.error("Failed to fetch color", colorId, error);
                        return [colorId, null];
                    }
                })
            );

            setColorsById(Object.fromEntries(colorResults));
        };

        loadColors();
    }, [shoppingCard, dispatch]);

    const products = (shoppingCard?.productItems || []).map((item) => ({
        ...item.product,
        cardItemId: item.cardItemId,
        quantity: item.quantity,
        color: colorsById[item.product?.colorId],
    }));

    if (!isOpen || !order) return null;


    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">

                <div className="border-b px-6 py-4 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-custom-green">
                        Order Details #{order.orderId}
                    </h2>

                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <i className="fa-solid fa-times"></i>
                    </button>
                </div>

                <div className="p-6 overflow-y-auto max-h-[75vh]">
                    <div className="space-y-3 mb-8">
                        <p><strong>Customer:</strong> {order.orderOwnerName || "N/A"}</p>
                        <p><strong>Email:</strong> {order.orderOwnerEmail || "N/A"}</p>
                        <p><strong>Status:</strong> {order.orderStatus || "-"}</p>
                        <p><strong>Products:</strong> {shoppingCard?.numProd ?? order.numProducts ?? products.length}</p>
                        <p><strong>Total:</strong> €{Number(shoppingCard?.total ?? order.total ?? 0).toFixed(2)}</p>
                        <p>
                            <strong>Date:</strong>{" "}
                            {order.dateCreated
                                ? new Date(order.dateCreated).toLocaleString("el-GR")
                                : "-"}
                        </p>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold mb-4 text-custom-green">
                            Products
                        </h3>

                        {isLoadingCard ? (
                            <p className="text-gray-500">Loading products...</p>
                        ) : products.length === 0 ? (
                            <p className="text-gray-500">No products found</p>
                        ) :  (
                            <div className="space-y-4">
                                {products.map((product) => (
                                    <div
                                        key={`${product.productID}-${product.cardItemId}`}
                                        className="border rounded-xl p-4 flex gap-4 items-center hover:shadow-md transition"
                                    >
                                        <img
                                            src={product.imageURL || "/placeholder-product.png"}
                                            alt={product.name || "Product"}
                                            className="w-24 h-24 rounded-lg border object-cover bg-gray-100"
                                        />

                                        <div className="flex-1">
                                            <h4 className="font-semibold text-lg">
                                                {product.name || "Unknown Product"}
                                            </h4>

                                            <div className="grid grid-cols-2 gap-y-2 mt-3 text-sm text-gray-700">
                                                <p>
                                                    <strong>ID:</strong> #{product.productID}
                                                </p>

                                                <p>
                                                    <strong>Price:</strong> €
                                                    {Number(product.price || 0).toFixed(2)}
                                                </p>
                                                <p><strong>Quantity:</strong> {product.quantity ?? "-"}</p>

                                                <p>
                                                    <strong>Line Total:</strong> €
                                                    {Number((product.price || 0) * (product.quantity || 1)).toFixed(2)}
                                                </p>

                                                <p>
                                                    <strong>Color:</strong>{" "}
                                                    {product.color?.colorName || product.color?.ColorName || product.colorId || "-"}
                                                </p>
                                                <p className="flex items-center gap-2">
                                                    <strong>Color Preview:</strong>
                                                    {product.color?.hexColor || product.color?.HexColor ? (
                                                        <span
                                                            className="w-5 h-5 rounded-full border inline-block"
                                                            style={{
                                                                backgroundColor: product.color?.hexColor || product.color?.HexColor,
                                                            }}
                                                            className="w-8 h-8 rounded-full border object-cover"
                                                        />
                                                    ) : (
                                                        "-"
                                                    )}
                                                </p>
                                                <p><strong>Textiles:</strong> {product.textiles || "-"}</p>
                                                <p><strong>Closing:</strong> {product.closingTextiles || "-"}</p>
                                                <p><strong>Plexis:</strong> {product.plexis || "-"}</p>
                                                <p><strong>Category:</strong> #{product.categoryRelId ?? "-"}</p>



                                                <p>
                                                    <strong>Rating:</strong>{" "}
                                                    {product.rating ?? "-"}
                                                </p>
                                            </div>

                                            {product.description && (
                                                <p className="mt-3 text-sm text-gray-600">
                                                    {product.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="border-t px-6 py-4 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ViewModal;